/**
 * Widget del gruppo "Soldi": fatturato, da incassare, fatture scadute,
 * andamento mensile, ricavi ricorrenti.
 *
 * I conti sono gli stessi della pagina Analisi aziendali (kpiFatturato.ts):
 * lo stesso numero non puo' venire diverso qui e la'. Fatturato, incassi e
 * costi sono senza IVA; i crediti aperti sono quello che il cliente deve
 * pagare, IVA compresa.
 */

import { Link } from 'react-router-dom';
import { BarChart3, CheckCircle2, Repeat } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { euroIntero } from '../../../lib/dashboard/formato';
import { aggregaFatture, statisticheAbbonamenti, type AggregatiAnalisi, type FatturaAnalisi } from '../../../lib/dashboard/kpiFatturato';
import { andamentoMensile, confrontoFatturato, rateScadute, type PeriodoFatturato } from '../../../lib/dashboard/kpiWidget';
import { useFonte, useOggi, useRicaricaFonte } from '../../../hooks/useFonte';
import { AggiornaDaFic } from '../../AggiornaDaFic';
import { RIGHE_ELENCO } from '../taglie';
import { Andamento, Etichetta, Fonte, Iniziali, NumeroGrande, Pillola, StatoVuoto, Variazione } from './parti';
import type { PropsWidget } from './tipi';

const MESI_LUNGHI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];

function descriviPeriodo(periodo: PeriodoFatturato, oggi: string): string {
  const anno = oggi.slice(0, 4);
  if (periodo === 'anno') return `Nel ${anno}, fino a oggi`;
  if (periodo === 'mese') return `A ${MESI_LUNGHI[Number(oggi.slice(5, 7)) - 1]}, fino a oggi`;
  return `Nel ${Math.floor((Number(oggi.slice(5, 7)) - 1) / 3) + 1}° trimestre ${anno}`;
}

/** I conti delle fatture: si usano in piu' widget. */
function aggregati(fatture: readonly FatturaAnalisi[], oggi: string): AggregatiAnalisi {
  return aggregaFatture(fatture, [], { oggi, annoCorrente: Number(oggi.slice(0, 4)) });
}

// ---------------------------------------------------------------------------

export function WidgetFatturato({ taglia, impostazioni }: PropsWidget<'fatturato'>) {
  const fatture = useFonte('fatture');
  const ricarica = useRicaricaFonte();
  const oggi = useOggi();

  return (
    <Fonte stato={fatture} onRiprova={() => ricarica('fatture')}>
      {(tutte) => {
        const c = confrontoFatturato(tutte, impostazioni.periodo, oggi);
        const annoPrima = Number(oggi.slice(0, 4)) - 1;
        const mesi = andamentoMensile(aggregati(tutte, oggi), 12, oggi).map((m) => m.emesso);
        return (
          <div className="relative flex h-full flex-col justify-end">
            <Andamento valori={mesi} className="absolute -bottom-4 -left-4 h-2/3 w-[calc(100%+2rem)]" />
            <div className="relative">
              <Etichetta>{descriviPeriodo(impostazioni.periodo, oggi)}</Etichetta>
              <NumeroGrande valore={euroIntero(c.valore)} taglia={taglia} className="mt-1" />
              {impostazioni.confronto && (
                <div className="mt-2 flex min-w-0 items-center gap-2">
                  <Variazione percentuale={c.variazione} rispetto={`sul ${annoPrima}`} />
                  {taglia === 'M' && <span className="truncate text-[11px] opacity-70">({annoPrima}: {euroIntero(c.precedente)})</span>}
                </div>
              )}
            </div>
          </div>
        );
      }}
    </Fonte>
  );
}

// ---------------------------------------------------------------------------

export function WidgetDaIncassare({ taglia, contesto }: PropsWidget<'da-incassare'>) {
  const fatture = useFonte('fatture');
  const ricarica = useRicaricaFonte();
  const oggi = useOggi();

  return (
    <Fonte stato={fatture} onRiprova={() => ricarica('fatture')}>
      {(tutte) => {
        const { aperto } = aggregati(tutte, oggi);
        if (aperto.totale <= 0) return <StatoVuoto icona={CheckCircle2} testo="Tutto incassato" sotto="Nessun credito aperto" />;
        const quotaScaduta = Math.min(1, aperto.scaduto / aperto.totale);
        return (
          <div className="flex h-full flex-col justify-end gap-2">
            <div className="min-w-0">
              <NumeroGrande valore={euroIntero(aperto.totale)} taglia={taglia} />
              {taglia === 'M' && <Etichetta className="mt-1">ancora da incassare, IVA compresa</Etichetta>}
            </div>
            <div className="flex h-2 flex-shrink-0 overflow-hidden rounded-full bg-white/25" title={`Scaduto ${euroIntero(aperto.scaduto)} su ${euroIntero(aperto.totale)}`}>
              <div className="rounded-full bg-white" style={{ width: `${Math.max(quotaScaduta * 100, aperto.scaduto > 0 ? 4 : 0)}%` }} />
            </div>
            <div className="flex min-w-0 items-center justify-between gap-2 text-[11px] font-semibold">
              <span className="flex min-w-0 items-center gap-1.5 truncate">
                <span className="h-2 w-2 flex-shrink-0 rounded-full bg-white" /> scaduto {euroIntero(aperto.scaduto)}
              </span>
              {taglia === 'M' && <AggiornaDaFic ruolo={contesto.ruolo} compatto onAggiornato={() => ricarica('fatture')} />}
            </div>
          </div>
        );
      }}
    </Fonte>
  );
}

// ---------------------------------------------------------------------------

export function WidgetFattureScadute({ taglia }: PropsWidget<'fatture-scadute'>) {
  const fatture = useFonte('fatture');
  const ricarica = useRicaricaFonte();
  const oggi = useOggi();

  return (
    <Fonte stato={fatture} onRiprova={() => ricarica('fatture')}>
      {(tutte) => {
        const scadute = rateScadute(aggregati(tutte, oggi).scadenzeAperte, oggi);
        if (scadute.length === 0) return <StatoVuoto icona={CheckCircle2} testo="Nessuna fattura scaduta" sotto="I clienti sono in regola" />;
        const totale = scadute.reduce((s, x) => s + x.importo, 0);
        const righe = taglia === 'M' ? 1 : RIGHE_ELENCO[taglia] - 1;
        return (
          <div className="flex h-full min-h-0 flex-col gap-2">
            <div className="flex items-baseline gap-2">
              <span className="font-display text-3xl font-bold leading-none text-red-600">{scadute.length}</span>
              <span className="truncate text-xs font-medium text-neutral-600">rate scadute · {euroIntero(totale)}</span>
            </div>
            <ul className="min-h-0 space-y-1.5 overflow-hidden">
              {scadute.slice(0, righe).map((s, i) => (
                <li key={`${s.fatturaId}-${i}`} className="flex items-center gap-2.5 rounded-2xl bg-white/80 px-2 py-1.5 shadow-sm ring-1 ring-rose-100">
                  <Iniziali nome={s.cliente} colore="bg-rose-100 text-rose-700" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-neutral-900">{s.cliente}</p>
                    <p className="truncate text-[11px] text-neutral-500">fattura {s.numero || '—'} · {euroIntero(s.importo)}</p>
                  </div>
                  <Pillola colore="bg-red-100 text-red-700" className="flex-shrink-0">{s.giorniRitardo} gg</Pillola>
                </li>
              ))}
            </ul>
            {scadute.length > righe && (
              <Link to="/analisi-aziendali" className="mt-auto text-[11px] font-semibold text-red-600 hover:underline">
                Vedi tutte le {scadute.length} rate scadute
              </Link>
            )}
          </div>
        );
      }}
    </Fonte>
  );
}

// ---------------------------------------------------------------------------

const SERIE = [
  { chiave: 'emesso', etichetta: 'Fatturato', punto: 'bg-rose-400', barra: 'bg-gradient-to-t from-primary-600 to-rose-400' },
  { chiave: 'incassato', etichetta: 'Incassato', punto: 'bg-emerald-400', barra: 'bg-gradient-to-t from-emerald-600 to-emerald-300' },
  { chiave: 'costi', etichetta: 'Costi', punto: 'bg-amber-300', barra: 'bg-gradient-to-t from-amber-500 to-yellow-300' },
] as const;

export function WidgetAndamentoMensile({ taglia, impostazioni }: PropsWidget<'andamento-mensile'>) {
  const fatture = useFonte('fatture');
  const costi = useFonte('costi');
  const ricarica = useRicaricaFonte();
  const oggi = useOggi();

  return (
    <Fonte stato={fatture} onRiprova={() => ricarica('fatture')}>
      {(tutteFatture) => (
        <Fonte stato={costi} onRiprova={() => ricarica('costi')}>
          {(tuttiCosti) => {
            const agg = aggregaFatture(tutteFatture, tuttiCosti, { oggi, annoCorrente: Number(oggi.slice(0, 4)) });
            const mesi = andamentoMensile(agg, taglia === 'M' ? 6 : impostazioni.mesi, oggi);
            const massimo = Math.max(1, ...mesi.flatMap((m) => [m.emesso, m.incassato, m.costi]));
            if (mesi.every((m) => !m.emesso && !m.incassato && !m.costi)) {
              return <StatoVuoto icona={BarChart3} testo="Nessun movimento in questi mesi" />;
            }
            const totale = mesi.reduce((s, m) => s + m.emesso, 0);
            return (
              <div className="flex h-full min-h-0 flex-col">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                  {SERIE.map((s) => (
                    <span key={s.chiave} className="flex items-center gap-1.5 opacity-80">
                      <span className={cn('h-2 w-2 rounded-full', s.punto)} /> {s.etichetta}
                    </span>
                  ))}
                  {taglia !== 'M' && (
                    <span className="ml-auto flex items-baseline gap-1.5" title="Fatturato dei mesi mostrati, senza IVA">
                      <span className="opacity-60">fatturato {mesi.length} mesi</span>
                      <span className="font-display text-base font-bold">{euroIntero(totale)}</span>
                    </span>
                  )}
                </div>
                <div className="relative mt-3 flex min-h-0 flex-1 items-end gap-1.5">
                  <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-5 top-0 flex flex-col justify-between">
                    {[0, 1, 2, 3].map((i) => <div key={i} className="border-t border-dashed border-white/10" />)}
                  </div>
                  {mesi.map((m, i) => {
                    const corrente = i === mesi.length - 1;
                    return (
                      <div key={m.chiave} className="relative flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1.5">
                        <div className={cn('flex h-full w-full items-end justify-center gap-[3px] rounded-lg px-0.5', corrente && 'bg-white/[0.06]')}>
                          {SERIE.map((s) => (
                            <div
                              key={s.chiave}
                              className={cn('w-full max-w-[12px] rounded-t-full', s.barra)}
                              style={{ height: `${Math.max(1.5, (Math.max(0, m[s.chiave]) / massimo) * 100)}%` }}
                              title={`${m.etichetta} · ${s.etichetta}: ${euroIntero(m[s.chiave])}`}
                            />
                          ))}
                        </div>
                        <span className={cn('truncate text-[10px]', corrente ? 'font-bold text-white' : 'text-white/45')}>{m.etichetta}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }}
        </Fonte>
      )}
    </Fonte>
  );
}

// ---------------------------------------------------------------------------

export function WidgetRicaviRicorrenti({ taglia }: PropsWidget<'ricavi-ricorrenti'>) {
  const abbonamenti = useFonte('abbonamenti');
  const ricarica = useRicaricaFonte();
  const oggi = useOggi();

  return (
    <Fonte stato={abbonamenti} onRiprova={() => ricarica('abbonamenti')}>
      {(tutti) => {
        const s = statisticheAbbonamenti(tutti, oggi);
        if (s.attivi === 0) {
          return <StatoVuoto icona={Repeat} testo="Nessun abbonamento attivo" azione={taglia === 'M' ? { etichetta: 'Aggiungi', percorso: '/subscriptions' } : undefined} />;
        }
        return (
          <div className="flex h-full flex-col justify-end">
            <div className="flex items-baseline gap-1.5">
              <NumeroGrande valore={euroIntero(s.mrr)} taglia={taglia} />
              <span className="text-sm font-semibold opacity-75">/mese</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Pillola>{euroIntero(s.arr)} l&apos;anno</Pillola>
              {taglia === 'M' && <Pillola>{s.attivi} attivi</Pillola>}
              {taglia === 'M' && s.inScadenza > 0 && <Pillola>{s.inScadenza} in scadenza</Pillola>}
            </div>
          </div>
        );
      }}
    </Fonte>
  );
}
